import { useCallback, useState } from 'react';

import { usersCheckPermissionCreate } from '~/data/generated/endpoints/users/users';
import type { CheckPermissionResponse } from '~/data/generated/model';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type Invoice, InvoiceKind } from '~/data/schemas/invoice';
import { userPermissionCheckSchema } from '~/data/schemas/userPermission';

interface UseUserPermissionCheckReturn {
  permissionResults: Record<string, CheckPermissionResponse>;
  permissionErrors: Record<string, string>;
  checkingPermissions: boolean;
  checkUserPermissions: (emails: string[], invoice: Invoice) => Promise<void>;
  hasPermission: (email: string) => boolean;
  clearPermissionData: () => void;
  setPermissionErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export const useUserPermissionCheck = (): UseUserPermissionCheckReturn => {
  const [permissionResults, setPermissionResults] = useState<
    Record<string, CheckPermissionResponse>
  >({});
  const [checkingPermissions, setCheckingPermissions] = useState(false);
  const [permissionErrors, setPermissionErrors] = useState<Record<string, string>>({});

  const checkUserPermissions = useCallback(async (emails: string[], invoice: Invoice) => {
    // Credits are never reviewed, so there is no review to notify about.
    const kind = invoice.kind;
    if (emails.length === 0 || kind === InvoiceKind.Credit) {
      setPermissionResults({});
      setPermissionErrors({});
      return;
    }

    setCheckingPermissions(true);
    const newResults: Record<string, CheckPermissionResponse> = {};
    const newErrors: Record<string, string> = {};

    try {
      await Promise.all(
        emails.map(async (email) => {
          try {
            const { data } = await usersCheckPermissionCreate({
              email,
              kind,
              id: invoice.id,
            });
            const result = parseApiResponse(userPermissionCheckSchema, data, 'UserPermissionCheck');
            if (!result.hasPermission) {
              newErrors[email] = 'does not have permission';
            }
            newResults[email] = result;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, error shape is untyped
          } catch (error: any) {
            console.error('Error checking permissions:', error);
            const errorMessage = error?.context?.data?.message || 'Failed to check permission';
            newErrors[email] = errorMessage;
            newResults[email] = {
              hasPermission: false,
              userEmail: email,
              kind: invoice.kind,
              resourceId: invoice.id,
            };
          }
        }),
      );

      setPermissionResults(newResults);
      setPermissionErrors(newErrors);
    } catch (error) {
      console.error('Error checking permissions:', error);
    } finally {
      setCheckingPermissions(false);
    }
  }, []);

  const hasPermission = useCallback(
    (email: string) => {
      return permissionResults[email]?.hasPermission === true;
    },
    [permissionResults],
  );

  const clearPermissionData = useCallback(() => {
    setPermissionResults({});
    setPermissionErrors({});
  }, []);

  return {
    permissionResults,
    permissionErrors,
    checkingPermissions,
    checkUserPermissions,
    hasPermission,
    clearPermissionData,
    setPermissionErrors,
  };
};
